import { makeObservable, observable } from 'mobx';
import { API } from '~/api';
import { GridStateModel } from '~/model/model.gridstate';
import { TranslationModel } from '~/model/model.translation';
import { IngredientCreateDto } from '~/shared/dto/admin/ingredient.dto';
import { Async } from '~/shared/tools/async';
import { Singleton } from '../shared/service/service.base';
import { ApiService } from './service.api';

@Singleton()
export class IngredientService {
	readonly items = observable.array([] as API.IngredientItem[], { deep: false });
	readonly async = new Async();
	readonly gridState = new GridStateModel('ingredient');

	@observable totalCount = 0;
	@observable.ref names: API.IngredientName[] = [];

	constructor(
		private api: ApiService,
		readonly tr: TranslationModel,
	) {
		makeObservable(this);
	}

	loadAndSubscribe = () => {
		this.reload();
		return this.gridState.subscribe(this.reload);
	};

	reload = () => {
		this.async.call(async () => {
			const { data } = await this.api.ingredientGetAll({
				filter: this.gridState.filterQuery,
				limit: this.gridState.pageSize,
				offset: this.gridState.offset,
				sort: this.gridState.sort,
				sortDir: this.gridState.sortDir,
			});
			return () => {
				this.items.replace(data.items);
				this.totalCount = data.total;
			};
		});
	};

	async getOne(id: number | undefined) {
		if (id === undefined) return;
		return this.async.exec(() => this.api.ingredientGetOne(id).then(resp => resp.data));
	}

	save(id: number | undefined, data: IngredientCreateDto) {
		return this.async.exec(async () => {
			await this.api.uploadImgs(data.images);
			if (id === undefined) {
				await this.api.ingredientCreate(data);
			} else {
				await this.api.ingredientUpdate(id, data);
			}
		});
	}

	delete = (id: number) => {
		return this.async.exec(() => this.api.ingredientDelete(id)).then(this.reload);
	};

	fetchNames = () => {
		this.async.call(async () => {
			const { data } = await this.api.ingredientGetNames();
			return () => (this.names = data.items);
		});
	};
}
